// ===========================================
// Dev Notes Page - Developer Knowledge Notebook
// ===========================================
// Capture, organize, and search your dev learnings
// Integrates with Note Builder for AI-powered knowledge collection

import React, { useState, useEffect, useCallback } from 'react';
import { notesAPI } from '../services/api';
import { useProjects } from '../context/ProjectContext';
import {
  Search,
  Plus,
  Pin,
  Archive,
  Edit2,
  Trash2,
  Loader,
  BookOpen,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Clock,
  Calendar,
  Hash,
  FolderOpen,
  X,
  Check,
  Bot,
  RefreshCw,
  Code,
  Database,
  Cloud,
  Shield,
  Zap,
  Bug,
  Gauge,
  Wrench,
  FileCode,
  Layers,
  Wand2,
  Brain,            // Icon for AI-generated pattern tags
  SlidersHorizontal, // Mobile filter trigger icon
  Filter            // Filter icon for mobile sheet
} from 'lucide-react';
import NoteBuilder from '../components/NoteBuilder';
import './DevNotes.scss';

// ===========================================
// Topic Configuration
// ===========================================
// Developer-focused topics for organizing knowledge
const TOPICS = {
  all: { label: 'All Notes', icon: BookOpen, color: 'var(--color-accent)' },
  general: { label: 'General', icon: FolderOpen, color: '#6b7280' },
  javascript: { label: 'JavaScript', icon: FileCode, color: '#f7df1e' },
  react: { label: 'React', icon: Layers, color: '#61dafb' },
  backend: { label: 'Backend', icon: Code, color: '#68a063' },
  database: { label: 'Database', icon: Database, color: '#4db33d' },
  devops: { label: 'DevOps', icon: Cloud, color: '#2496ed' },
  ai: { label: 'AI & LLMs', icon: Sparkles, color: '#a855f7' },
  architecture: { label: 'Architecture', icon: Layers, color: '#ff6b6b' },
  debugging: { label: 'Debugging', icon: Bug, color: '#ef4444' },
  performance: { label: 'Performance', icon: Gauge, color: '#22c55e' },
  security: { label: 'Security', icon: Shield, color: '#f59e0b' },
  tools: { label: 'Tools', icon: Wrench, color: '#06b6d4' }
};

// ===========================================
// Sort Options
// ===========================================
const SORT_OPTIONS = [
  { value: 'recent', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'updated', label: 'Recently Updated' },
  { value: 'alpha', label: 'Alphabetical' }
];

const DevNotes = () => {
  // ===========================================
  // State Management
  // ===========================================
  const { currentProject } = useProjects();
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, pinned: 0, fromAI: 0 });
  const [topicStats, setTopicStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');  // Debounced search to prevent API spam
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  
  // ===========================================
  // AI Tag Category Filters - Dynamic & Organic
  // ===========================================
  // These categories grow as users create notes and AI generates tags
  const [aiTagCategories, setAiTagCategories] = useState({
    outcomes: [],    // What notes document/teach (e.g., "debugging-technique", "setup-guide")
    patterns: [],    // Development concepts (e.g., "error-handling", "state-management")
    technologies: [] // Tech stack items (e.g., "react", "mongodb", "typescript")
  });
  const [selectedOutcome, setSelectedOutcome] = useState(null);   // Filter by outcome tag
  const [selectedPattern, setSelectedPattern] = useState(null);   // Filter by pattern tag
  const [selectedTech, setSelectedTech] = useState(null);         // Filter by technology tag
  
  // Sidebar section collapse state for AI tag categories
  const [outcomesCollapsed, setOutcomesCollapsed] = useState(false);
  const [patternsCollapsed, setPatternsCollapsed] = useState(true);
  const [techCollapsed, setTechCollapsed] = useState(true);
  
  // Modal state for create/edit
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  
  // Expanded notes (for viewing full content)
  const [expandedIds, setExpandedIds] = useState(new Set());
  
  // ===========================================
  // Note Builder Modal State
  // ===========================================
  const [showNoteBuilder, setShowNoteBuilder] = useState(false);
  
  // Mobile filter sheet state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // ===========================================
  // Fetch Notes from API
  // ===========================================
  // Uses hybrid search (full-text + vector) when search query is present
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Always fetch topic stats
      const statsRes = await notesAPI.getTopicStats();
      if (statsRes.data.success) {
        setTopicStats(statsRes.data.data || []);
      }
      
      // ===========================================
      // Hybrid Search Mode (when search query present)
      // ===========================================
      // Uses dedicated search endpoint with full-text + vector search
      // Backend requires minimum 2 characters for search
      if (debouncedSearch.trim().length >= 2) {
        const searchRes = await notesAPI.search(debouncedSearch.trim(), { limit: 50 });
        
        if (searchRes.data.success) {
          // Hybrid search returns { results, meta } structure
          setNotes(searchRes.data.data.results || []);
          // Note: Stats not included in search results, keep existing stats
        }
        setLoading(false);
        return;
      }
      
      // ===========================================
      // Standard List Mode (browsing with filters)
      // ===========================================
      // Build query params based on current filters
      const params = { sort: sortBy, archived: showArchived };
      
      if (selectedTopic !== 'all') {
        params.topic = selectedTopic;
      }
      
      // ===========================================
      // AI Tag Category Filters (Dynamic Categories)
      // ===========================================
      // Filter by AI-generated semantic tags
      if (selectedOutcome) {
        params.outcomeTag = selectedOutcome;
      }
      if (selectedPattern) {
        params.patternTag = selectedPattern;
      }
      if (selectedTech) {
        params.techTag = selectedTech;
      }
      
      const notesRes = await notesAPI.list(params);
      
      if (notesRes.data.success) {
        setNotes(notesRes.data.data.notes || []);
        setStats(notesRes.data.data.stats || { total: 0, pinned: 0, fromAI: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      
      // Only show error for actual server errors (500+)
      // For empty data or network issues, show empty state gracefully
      const status = err.response?.status;
      if (status >= 500) {
        setError('Failed to load notes. Please try again.');
      }
      // Other errors fall through to empty state UI ("No notes yet")
    }
    
    setLoading(false);
  }, [selectedTopic, debouncedSearch, sortBy, showArchived, selectedOutcome, selectedPattern, selectedTech]);

  // Debounce search query to prevent API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);  // 300ms debounce delay
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // ===========================================
  // Fetch AI Tag Categories for Sidebar
  // ===========================================
  // These categories grow organically as users create notes
  const fetchAITagCategories = async () => {
    try {
      const response = await notesAPI.getAITagCategories();
      if (response.data.success) {
        setAiTagCategories(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch AI tag categories:', err);
    }
  };
  
  // Fetch on mount
  useEffect(() => {
    fetchAITagCategories();
  }, []);

  // ===========================================
  // Event Handlers
  // ===========================================
  
  // Toggle pin status for a note
  const handleTogglePin = async (note, e) => {
    e.stopPropagation();
    try {
      await notesAPI.togglePin(note._id);
      setNotes(prev => prev.map(n => 
        n._id === note._id ? { ...n, isPinned: !n.isPinned } : n
      ));
      // Update pinned count in stats
      setStats(prev => ({
        ...prev,
        pinned: note.isPinned ? prev.pinned - 1 : prev.pinned + 1
      }));
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };
  
  // Archive/unarchive a note
  const handleToggleArchive = async (note, e) => {
    e.stopPropagation();
    try {
      await notesAPI.toggleArchive(note._id);
      // Remove from current list if archiving (and not viewing archived)
      if (!note.isArchived && !showArchived) {
        setNotes(prev => prev.filter(n => n._id !== note._id));
        setStats(prev => ({ ...prev, total: prev.total - 1 }));
      } else {
        // Just toggle the state
        setNotes(prev => prev.map(n => 
          n._id === note._id ? { ...n, isArchived: !n.isArchived, isPinned: false } : n
        ));
      }
    } catch (err) {
      console.error('Failed to archive:', err);
    }
  };
  
  // Delete a note permanently
  const handleDelete = async (note, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${note.title}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await notesAPI.delete(note._id);
      setNotes(prev => prev.filter(n => n._id !== note._id));
      setStats(prev => ({ 
        ...prev, 
        total: prev.total - 1,
        pinned: note.isPinned ? prev.pinned - 1 : prev.pinned,
        fromAI: note.source?.type === 'ai_chat' ? prev.fromAI - 1 : prev.fromAI
      }));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };
  
  // Open create modal
  const handleCreate = () => {
    setEditingNote(null);
    setShowModal(true);
  };
  
  // ===========================================
  // Save from Note Builder (opens edit modal)
  // ===========================================
  // Creates a new note from AI content and opens modal for editing
  const handleSaveFromNoteBuilder = async ({ content, topic }) => {
    // Generate title from first 50 chars (strip markdown)
    const cleanContent = content.replace(/\*\*/g, '').replace(/^• /gm, '');
    const title = cleanContent.substring(0, 50).trim() + 
      (cleanContent.length > 50 ? '...' : '');
    
    // Create the note immediately
    try {
      const response = await notesAPI.create({
        title,
        content,
        topic: topic || 'ai',
        tags: [],
        source: {
          type: 'ai_chat',
          projectId: currentProject?._id || currentProject?.id
        }
      });
      
      if (response.data.success) {
        const newNote = response.data.data;
        // Add to notes list
        setNotes(prev => [newNote, ...prev]);
        setStats(prev => ({ 
          ...prev, 
          total: prev.total + 1,
          fromAI: prev.fromAI + 1 
        }));
        
        // Close Note Builder and open edit modal so user can customize
        setShowNoteBuilder(false);
        setEditingNote(newNote);
        setShowModal(true);
        
        // Refresh AI tag categories after save - new tags may have been generated
        setTimeout(fetchAITagCategories, 2000);
        
        return { success: true, note: newNote };
      }
    } catch (err) {
      console.error('Failed to save from Note Builder:', err);
      return { success: false };
    }
  };
  
  // Open edit modal with existing note
  const handleEdit = (note, e) => {
    e.stopPropagation();
    setEditingNote(note);
    setShowModal(true);
  };
  
  // Save note (create or update)
  const handleSave = async (noteData) => {
    try {
      if (editingNote) {
        // Update existing note
        const response = await notesAPI.update(editingNote._id, noteData);
        if (response.data.success) {
          setNotes(prev => prev.map(n => 
            n._id === editingNote._id ? response.data.data : n
          ));
        }
      } else {
        // Create new note
        const response = await notesAPI.create(noteData);
        if (response.data.success) {
          setNotes(prev => [response.data.data, ...prev]);
          setStats(prev => ({ ...prev, total: prev.total + 1 }));
        }
      }
      setShowModal(false);
      setEditingNote(null);
      
      // Refresh AI tag categories after save - new tags may have been generated
      // Small delay to allow backend AI tag generation to complete
      setTimeout(fetchAITagCategories, 2000);
    } catch (err) {
      console.error('Failed to save:', err);
    }
  };
  
  // Toggle expanded state for a note card
  const toggleExpanded = (noteId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };
  
  // Get topic count from aggregated stats
  const getTopicCount = (topicKey) => {
    const stat = topicStats.find(s => s._id === topicKey);
    return stat?.count || 0;
  };
  
  // ===========================================
  // Date Formatting Helpers
  // ===========================================
  
  // Format relative time (e.g., "5m ago", "2h ago", "3d ago")
  const formatRelativeTime = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Format full date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // ===========================================
  // Mobile Filter Helpers
  // ===========================================
  // Count active filters for the mobile filter badge
  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedTopic !== 'all') count++;
    if (showArchived) count++;
    if (selectedOutcome) count++;
    if (selectedPattern) count++;
    if (selectedTech) count++;
    return count;
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedTopic('all');
    setShowArchived(false);
    setSelectedOutcome(null);
    setSelectedPattern(null);
    setSelectedTech(null);
  };

  // ===========================================
  // Render Component
  // ===========================================
  return (
    <div className="dev-notes">
      {/* Header Section - Tactical Terminal Style */}
      <div className="dev-notes__header">
        <div className="dev-notes__header-info">
          <h1>Dev Notes</h1>
          <p>Your developer knowledge notebook</p>
        </div>
        <div className="dev-notes__header-actions">
          <button className="btn-secondary" onClick={fetchNotes} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button 
            className="btn-note-builder"
            onClick={() => setShowNoteBuilder(true)}
          >
            <Wand2 size={16} />
            Note Builder
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            <Plus size={16} />
            New Note
          </button>
        </div>
      </div>

      {/* Stats Bar - Quick overview of notes */}
      <div className="dev-notes__stats">
        <div className="dev-notes__stat">
          <BookOpen size={16} />
          <span className="dev-notes__stat-value">{stats.total}</span>
          <span className="dev-notes__stat-label">Notes</span>
        </div>
        <div className="dev-notes__stat">
          <Pin size={16} />
          <span className="dev-notes__stat-value">{stats.pinned}</span>
          <span className="dev-notes__stat-label">Pinned</span>
        </div>
        <div className="dev-notes__stat">
          <Sparkles size={16} />
          <span className="dev-notes__stat-value">{stats.fromAI}</span>
          <span className="dev-notes__stat-label">From AI</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="dev-notes__content">
        {/* Sidebar - Topic Navigation */}
        <aside className="dev-notes__sidebar">
          <h3>Topics</h3>
          <ul className="dev-notes__topic-list">
            {Object.entries(TOPICS).map(([key, topic]) => {
              const Icon = topic.icon;
              // Show total for 'all', otherwise get count from stats
              const count = key === 'all' ? stats.total : getTopicCount(key);
              
              return (
                <li key={key}>
                  <button
                    className={`dev-notes__topic-btn ${selectedTopic === key ? 'active' : ''}`}
                    onClick={() => setSelectedTopic(key)}
                  >
                    <Icon 
                      size={14} 
                      style={{ color: selectedTopic === key ? topic.color : undefined }} 
                    />
                    <span>{topic.label}</span>
                    {count > 0 && (
                      <span className="dev-notes__topic-count">{count}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          
          {/* Archive Toggle */}
          <div className="dev-notes__archive-toggle">
            <button
              className={showArchived ? 'active' : ''}
              onClick={() => setShowArchived(!showArchived)}
            >
              <Archive size={14} />
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </button>
          </div>

          {/* ===========================================
              AI TAG CATEGORIES - Dynamic & Organic Filtering
              Categories grow as users create notes & AI tags them
          =========================================== */}
          <div className="dev-notes__ai-categories">
            <h3>AI Categories</h3>
            
            {/* All Notes - clears all AI tag filters */}
            <button
              className={`dev-notes__ai-category-btn dev-notes__ai-category-btn--all ${!selectedOutcome && !selectedPattern && !selectedTech ? 'active' : ''}`}
              onClick={() => { 
                setSelectedOutcome(null);
                setSelectedPattern(null);
                setSelectedTech(null);
              }}
            >
              <BookOpen size={14} />
              <span>All Notes</span>
            </button>
            
            {/* OUTCOMES - What notes document/teach */}
            <div className="dev-notes__ai-category-group">
              <button 
                className="dev-notes__ai-category-toggle"
                onClick={() => setOutcomesCollapsed(!outcomesCollapsed)}
              >
                {outcomesCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                <Sparkles size={12} style={{ color: '#ec4899' }} />
                <span>Outcomes</span>
                {aiTagCategories.outcomes.length > 0 && (
                  <span className="dev-notes__ai-category-count">
                    {aiTagCategories.outcomes.length}
                  </span>
                )}
              </button>
              {!outcomesCollapsed && (
                <ul className="dev-notes__ai-category-list">
                  {aiTagCategories.outcomes.map(({ tag, count }) => (
                    <li key={`outcome-${tag}`}>
                      <button
                        className={`dev-notes__ai-tag-btn ${selectedOutcome === tag ? 'active' : ''}`}
                        onClick={() => setSelectedOutcome(selectedOutcome === tag ? null : tag)}
                        title={`Filter by outcome: ${tag}`}
                      >
                        <Sparkles size={10} style={{ color: selectedOutcome === tag ? '#ec4899' : 'var(--color-text-muted)' }} />
                        <span>{tag}</span>
                        <span className="dev-notes__ai-tag-count">{count}</span>
                      </button>
                    </li>
                  ))}
                  {aiTagCategories.outcomes.length === 0 && (
                    <li className="dev-notes__ai-category-empty">No outcomes yet</li>
                  )}
                </ul>
              )}
            </div>

            {/* PATTERNS - Development concepts & approaches */}
            <div className="dev-notes__ai-category-group">
              <button 
                className="dev-notes__ai-category-toggle"
                onClick={() => setPatternsCollapsed(!patternsCollapsed)}
              >
                {patternsCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                <Brain size={12} style={{ color: '#a855f7' }} />
                <span>Patterns</span>
                {aiTagCategories.patterns.length > 0 && (
                  <span className="dev-notes__ai-category-count">
                    {aiTagCategories.patterns.length}
                  </span>
                )}
              </button>
              {!patternsCollapsed && (
                <ul className="dev-notes__ai-category-list">
                  {aiTagCategories.patterns.map(({ tag, count }) => (
                    <li key={`pattern-${tag}`}>
                      <button
                        className={`dev-notes__ai-tag-btn ${selectedPattern === tag ? 'active' : ''}`}
                        onClick={() => setSelectedPattern(selectedPattern === tag ? null : tag)}
                        title={`Filter by pattern: ${tag}`}
                      >
                        <Brain size={10} style={{ color: selectedPattern === tag ? '#a855f7' : 'var(--color-text-muted)' }} />
                        <span>{tag}</span>
                        <span className="dev-notes__ai-tag-count">{count}</span>
                      </button>
                    </li>
                  ))}
                  {aiTagCategories.patterns.length === 0 && (
                    <li className="dev-notes__ai-category-empty">No patterns yet</li>
                  )}
                </ul>
              )}
            </div>

            {/* TECH - Technologies & tools */}
            <div className="dev-notes__ai-category-group">
              <button 
                className="dev-notes__ai-category-toggle"
                onClick={() => setTechCollapsed(!techCollapsed)}
              >
                {techCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                <Zap size={12} style={{ color: '#00d4ff' }} />
                <span>Tech</span>
                {aiTagCategories.technologies.length > 0 && (
                  <span className="dev-notes__ai-category-count">
                    {aiTagCategories.technologies.length}
                  </span>
                )}
              </button>
              {!techCollapsed && (
                <ul className="dev-notes__ai-category-list">
                  {aiTagCategories.technologies.map(({ tag, count }) => (
                    <li key={`tech-${tag}`}>
                      <button
                        className={`dev-notes__ai-tag-btn ${selectedTech === tag ? 'active' : ''}`}
                        onClick={() => setSelectedTech(selectedTech === tag ? null : tag)}
                        title={`Filter by technology: ${tag}`}
                      >
                        <Zap size={10} style={{ color: selectedTech === tag ? '#00d4ff' : 'var(--color-text-muted)' }} />
                        <span>{tag}</span>
                        <span className="dev-notes__ai-tag-count">{count}</span>
                      </button>
                    </li>
                  ))}
                  {aiTagCategories.technologies.length === 0 && (
                    <li className="dev-notes__ai-category-empty">No tech tags yet</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </aside>

        {/* Main Panel - Notes List */}
        <main className="dev-notes__main">
          {/* Toolbar - Search and Sort */}
          <div className="dev-notes__toolbar">
            <div className="dev-notes__search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  className="dev-notes__search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            
            {/* Sort Dropdown */}
            <select 
              className="dev-notes__sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Error State */}
          {error && (
            <div className="dev-notes__error">
              <p>{error}</p>
              <button onClick={fetchNotes}>Try Again</button>
            </div>
          )}

          {/* Loading State */}
          {loading && notes.length === 0 && (
            <div className="dev-notes__loading">
              <Loader className="animate-spin" size={24} />
              <p>Loading notes...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && notes.length === 0 && !error && (
            <div className="dev-notes__empty">
              <BookOpen size={48} />
              <h3>No notes yet</h3>
              <p>
                {searchQuery 
                  ? 'No notes match your search. Try different keywords.'
                  : showArchived 
                    ? 'No archived notes found.'
                    : 'Start capturing your dev knowledge and AI insights'}
              </p>
              {!searchQuery && !showArchived && (
                <button className="btn-primary" onClick={handleCreate}>
                  <Plus size={16} />
                  Create First Note
                </button>
              )}
            </div>
          )}

          {/* Notes List */}
          <div className="dev-notes__list">
            {notes.map(note => {
              const isExpanded = expandedIds.has(note._id);
              const topicInfo = TOPICS[note.topic] || TOPICS.general;
              const TopicIcon = topicInfo.icon;
              
              return (
                <div 
                  key={note._id}
                  className={`dev-notes__card ${note.isPinned ? 'pinned' : ''} ${isExpanded ? 'expanded' : ''} ${note.isArchived ? 'archived' : ''}`}
                  onClick={() => toggleExpanded(note._id)}
                >
                  {/* Card Header - Always visible */}
                  <div className="dev-notes__card-header">
                    {/* Expand indicator */}
                    <div className={`dev-notes__expand-icon ${isExpanded ? 'expanded' : ''}`}>
                      <ChevronRight size={14} />
                    </div>
                    
                    {/* Pin indicator */}
                    {note.isPinned && (
                      <Pin size={12} className="dev-notes__pin-indicator" />
                    )}
                    
                    {/* AI source indicator */}
                    {note.source?.type === 'ai_chat' && (
                      <Bot size={12} className="dev-notes__ai-indicator" title="Saved from AI Chat" />
                    )}
                    
                    {/* Title */}
                    <h4 className="dev-notes__card-title">{note.title}</h4>
                    
                    {/* Compact meta info */}
                    <div className="dev-notes__card-meta">
                      <span 
                        className="dev-notes__card-topic"
                        style={{ color: topicInfo.color }}
                      >
                        <TopicIcon size={10} />
                        {topicInfo.label}
                      </span>
                      <span className="dev-notes__card-time">
                        <Clock size={10} />
                        {formatRelativeTime(note.createdAt)}
                      </span>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="dev-notes__card-actions">
                      <button
                        className={`dev-notes__action-btn ${note.isPinned ? 'active' : ''}`}
                        onClick={(e) => handleTogglePin(note, e)}
                        title={note.isPinned ? 'Unpin' : 'Pin to top'}
                      >
                        <Pin size={14} />
                      </button>
                      <button
                        className="dev-notes__action-btn"
                        onClick={(e) => handleEdit(note, e)}
                        title="Edit note"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="dev-notes__action-btn"
                        onClick={(e) => handleToggleArchive(note, e)}
                        title={note.isArchived ? 'Unarchive' : 'Archive'}
                      >
                        <Archive size={14} />
                      </button>
                      <button
                        className="dev-notes__action-btn dev-notes__action-btn--danger"
                        onClick={(e) => handleDelete(note, e)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="dev-notes__card-content">
                      {/* Note body with preserved whitespace */}
                      <div className="dev-notes__card-body">
                        {note.content}
                      </div>
                      
                      {/* Tags */}
                      {note.tags?.length > 0 && (
                        <div className="dev-notes__card-tags">
                          {note.tags.map(tag => (
                            <span key={tag} className="dev-notes__tag">
                              <Hash size={10} />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Footer with timestamps */}
                      <div className="dev-notes__card-footer">
                        <span>
                          <Calendar size={12} />
                          Created {formatDate(note.createdAt)}
                        </span>
                        {note.updatedAt !== note.createdAt && (
                          <span>
                            <Clock size={12} />
                            Updated {formatRelativeTime(note.updatedAt)}
                          </span>
                        )}
                        {note.source?.projectId && (
                          <span className="dev-notes__card-project">
                            <Zap size={12} />
                            {note.source.projectId.name || 'Project'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </main>
      </div>

      {/* Note Builder Modal - AI-powered note creation */}
      <NoteBuilder
        isOpen={showNoteBuilder}
        onClose={() => setShowNoteBuilder(false)}
        onSaveAsNote={handleSaveFromNoteBuilder}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <NoteModal
          note={editingNote}
          onClose={() => {
            setShowModal(false);
            setEditingNote(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* ===========================================
          Mobile Filter Sheet - Bottom sheet for mobile filtering
          Only visible on screens < 900px
      =========================================== */}
      
      {/* Mobile Filter Trigger Button */}
      <button 
        className="mobile-filter-trigger"
        onClick={() => setMobileFilterOpen(true)}
      >
        <SlidersHorizontal size={16} />
        Filters
        {getActiveFilterCount() > 0 && (
          <span className="mobile-filter-trigger__badge">{getActiveFilterCount()}</span>
        )}
      </button>

      {/* Mobile Filter Backdrop */}
      <div 
        className={`mobile-filter-backdrop ${mobileFilterOpen ? 'visible' : ''}`}
        onClick={() => setMobileFilterOpen(false)}
      />

      {/* Mobile Filter Sheet */}
      <div className={`mobile-filter-sheet ${mobileFilterOpen ? 'open' : ''}`}>
        <div className="mobile-filter-sheet__header">
          <h3>
            <Filter size={16} />
            Filter Notes
          </h3>
          <button 
            className="mobile-filter-sheet__close"
            onClick={() => setMobileFilterOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        <div className="mobile-filter-sheet__content">
          {/* Quick Filters */}
          <div className="mobile-filter-sheet__section">
            <h4>Quick Filters</h4>
            <div className="mobile-filter-sheet__options">
              <button 
                className={`mobile-filter-sheet__option ${showArchived ? 'active' : ''}`}
                onClick={() => setShowArchived(!showArchived)}
              >
                <Archive size={14} />
                Archived
              </button>
            </div>
          </div>

          {/* Topics */}
          <div className="mobile-filter-sheet__section">
            <h4>Topics</h4>
            <div className="mobile-filter-sheet__list">
              {Object.entries(TOPICS).map(([key, topic]) => {
                const Icon = topic.icon;
                const count = key === 'all' ? stats.total : getTopicCount(key);
                
                return (
                  <button
                    key={key}
                    className={`mobile-filter-sheet__list-item ${selectedTopic === key ? 'active' : ''}`}
                    onClick={() => setSelectedTopic(key)}
                  >
                    <Icon size={14} style={{ color: topic.color }} />
                    <span>{topic.label}</span>
                    {count > 0 && (
                      <span className="mobile-filter-sheet__count">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Outcomes */}
          {aiTagCategories.outcomes.length > 0 && (
            <div className="mobile-filter-sheet__section">
              <h4>
                <Sparkles size={12} style={{ color: '#ec4899' }} />
                Outcomes ({aiTagCategories.outcomes.length})
              </h4>
              <div className="mobile-filter-sheet__list">
                {aiTagCategories.outcomes.slice(0, 10).map(({ tag, count }) => (
                  <button
                    key={tag}
                    className={`mobile-filter-sheet__list-item ${selectedOutcome === tag ? 'active' : ''}`}
                    onClick={() => setSelectedOutcome(selectedOutcome === tag ? null : tag)}
                  >
                    <Sparkles size={12} />
                    <span>{tag}</span>
                    <span className="mobile-filter-sheet__count">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Patterns */}
          {aiTagCategories.patterns.length > 0 && (
            <div className="mobile-filter-sheet__section">
              <h4>
                <Brain size={12} style={{ color: '#a855f7' }} />
                Patterns ({aiTagCategories.patterns.length})
              </h4>
              <div className="mobile-filter-sheet__list">
                {aiTagCategories.patterns.slice(0, 10).map(({ tag, count }) => (
                  <button
                    key={tag}
                    className={`mobile-filter-sheet__list-item ${selectedPattern === tag ? 'active' : ''}`}
                    onClick={() => setSelectedPattern(selectedPattern === tag ? null : tag)}
                  >
                    <Brain size={12} />
                    <span>{tag}</span>
                    <span className="mobile-filter-sheet__count">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Technologies */}
          {aiTagCategories.technologies.length > 0 && (
            <div className="mobile-filter-sheet__section">
              <h4>
                <Zap size={12} style={{ color: '#00d4ff' }} />
                Technologies ({aiTagCategories.technologies.length})
              </h4>
              <div className="mobile-filter-sheet__list">
                {aiTagCategories.technologies.slice(0, 10).map(({ tag, count }) => (
                  <button
                    key={tag}
                    className={`mobile-filter-sheet__list-item ${selectedTech === tag ? 'active' : ''}`}
                    onClick={() => setSelectedTech(selectedTech === tag ? null : tag)}
                  >
                    <Zap size={12} />
                    <span>{tag}</span>
                    <span className="mobile-filter-sheet__count">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mobile-filter-sheet__actions">
          <button 
            className="mobile-filter-sheet__clear"
            onClick={clearAllFilters}
          >
            Clear All
          </button>
          <button 
            className="mobile-filter-sheet__apply"
            onClick={() => setMobileFilterOpen(false)}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// Note Modal Component
// ===========================================
// Modal for creating and editing notes
const NoteModal = ({ note, onClose, onSave }) => {
  // Form state
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [topic, setTopic] = useState(note?.topic || 'general');
  const [tags, setTags] = useState(note?.tags?.join(', ') || '');
  const [saving, setSaving] = useState(false);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    
    setSaving(true);
    await onSave({
      title: title.trim(),
      content: content.trim(),
      topic,
      tags: tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
    });
    setSaving(false);
  };
  
  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  
  return (
    <div className="dev-notes__modal-overlay" onClick={onClose}>
      <div className="dev-notes__modal" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="dev-notes__modal-header">
          <h3>
            <BookOpen size={18} />
            {note ? 'Edit Note' : 'New Note'}
          </h3>
          <button className="dev-notes__modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        
        {/* Modal Form */}
        <form onSubmit={handleSubmit}>
          <div className="dev-notes__modal-body">
            {/* Title Input */}
            <div className="dev-notes__form-group">
              <label htmlFor="note-title">Title</label>
              <input
                id="note-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's this note about?"
                required
                autoFocus
                maxLength={200}
              />
            </div>
            
            {/* Topic Select */}
            <div className="dev-notes__form-group">
              <label htmlFor="note-topic">Topic</label>
              <select 
                id="note-topic"
                value={topic} 
                onChange={(e) => setTopic(e.target.value)}
              >
                {Object.entries(TOPICS)
                  .filter(([key]) => key !== 'all')
                  .map(([key, t]) => (
                    <option key={key} value={key}>{t.label}</option>
                  ))
                }
              </select>
            </div>
            
            {/* Content Textarea */}
            <div className="dev-notes__form-group">
              <label htmlFor="note-content">Content</label>
              <textarea
                id="note-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your note here... Markdown is supported."
                rows={12}
                required
                maxLength={10000}
              />
              <span className="dev-notes__char-count">
                {content.length} / 10,000
              </span>
            </div>
            
            {/* Tags Input */}
            <div className="dev-notes__form-group">
              <label htmlFor="note-tags">Tags (comma-separated)</label>
              <input
                id="note-tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="react, hooks, optimization, tips..."
              />
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="dev-notes__modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={saving || !title.trim() || !content.trim()}
            >
              {saving ? (
                <Loader size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              {note ? 'Save Changes' : 'Create Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DevNotes;

